import { Box, Button, Flex, Text } from "@chakra-ui/react";
import { Checkbox, Modal, Popover, Table } from "antd";
import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { AiOutlinePlus } from "react-icons/ai";
import { MdOutlineAccountTree } from "react-icons/md";
import moment from "moment-timezone";
import { useToastWrapper } from "../../Wrapper/toastWrapper";
import { AdminSeller } from "../../Interface/AdminInterfaces";
import { AdminAPI } from "../../Apis/adminAPI";
import Loader from "../../Components/Loader/Loader";
import Pagination from "../../Components/Pagination/Pagination";
import { validateEmail } from "../../Utils/Validator";
import { filterCountries } from "../../Utils/countries";
import { runVerificationCheck } from "../../Utils/verificationCheck";
import { errorHandler } from "../../Utils/handler";
import SellerDeleteModal from "./SellerModals/SellerDeleteModal";
import SellerAssignIndustryModal from "./SellerModals/SellerAssignIndustryModal";
import SellerOverrideModal from "./SellerModals/SellerOverrideModal";
import SellerBulkUploadModal from "./SellerModals/SellerBulkUploadModal";
import SellerRenewModal, { SellerRenewData } from "./SellerModals/SellerRenewModal"; // Claude: payment modal on plan assign/renew 25-Apr-2026
import { AdminSellerAPI } from "../../Apis/adminSellerAPI"; // Claude: 25-Apr-2026
import { DepartmentsAPI } from "../../Apis/departmentsAPI"; // Claude: department options 16-Apr-2026
import SellerAdvancedSearch from "./SellerAdvancedSearch";

// Claude: split from monolithic Seller.tsx into orchestrator + sub-components 23-03-2026

type Props = {};

const mandatoryFields = ["companyName", "personName", "personEmail"];

// Claude: column chooser — fixed cols always shown; defaults match prior visible set 14-04-2026
const FIXED_SELLER_COLS = new Set(["companyName", "actions"]);
const DEFAULT_SELLER_COLS = ["companyName", "companyHqCountry", "personName", "personEmail", "personPhoneNumber", "planName", "verificationStatus", "actions"];

const Sellers = (_props: Props) => {
  const PAGESIZE = 20;
  const { error, success } = useToastWrapper();
  const navigate = useNavigate();
  const location = useLocation();
  // Claude: skip the initial empty fetch when returning from edit — restore effect will update appliedSearch and re-trigger the fetch 28-04-2026
  const skipInitialFetchRef = useRef(!!(location.state as any)?.restoredSearch);
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isBulkConfirmModalVisible, setIsBulkConfirmModalVisible] = useState(false);
  const [eventName] = useState("");

  const [data, setData] = useState<any>([]);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [selectedRows, setSelectedRows] = useState<any>([]);
  const [sortEventId, setSortEventId] = useState("");
  const [pendingEventId, setPendingEventId] = useState("");

  const showModal = () => setIsModalVisible(true);
  const handleOk = () => setIsModalVisible(false);

  // Bulk upload
  const [isBulkModalVisible, setIsBulkModalVisible] = useState(false);
  const [bulkUploadDone, setBulkUploadDone] = useState(false);
  const [bulkDocument, setBulkDocument] = useState<any>("");
  const [fileInputKey, setFileInputKey] = useState(Date.now());
  const [bulkEventId, setBulkEventId] = useState("");

  const showBulkModal = () => {
    setBulkDocument(""); setBulkEventId(""); setFileInputKey(Date.now());
    setBulkUploadDone(false); setIsBulkModalVisible(true);
  };
  const handleBulkOk = () => setIsBulkModalVisible(false);
  const showBulkConfirmModal = () => setIsBulkConfirmModalVisible(true);
  const hideBulkConfirmModal = () => { setIsBulkConfirmModalVisible(false); setBulkUploadDone(false); };

  // Delete modal
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const showDeleteModal = () => setIsDeleteModalOpen(true);
  const handleDeleteModalOk = () => setIsDeleteModalOpen(false);

  const timezones = moment.tz.names();

  // Form data
  const [formData, setFormData] = useState<AdminSeller>({
    eventId: "", companyName: "", companyHqCity: "", companyHqState: "",
    companyHqCountry: "", companyUrl: "", personName: "", personEmail: "",
    personPhoneNumber: "", personDesignation: "", companyTimezone: "",
    sellerBoothNumberIdentifier: [], productTypeTags: [],
    subscriptionPlanId: "", rewardPlanId: "", industryIds: [], targetIndustryIds: [],
    targetCountries: [], targetDepartments: [], // Claude: visibility targeting 16-Apr-2026
    companyInfo: "", sellerCompanyType: [], sellerCompanyTypeOther: "", adminNotes: "", // Claude: 14-04-2026 / 20-04-2026
  });
  const [edit, setEdit] = useState(false);
  const [editId, setEditId] = useState("");
  const [sellerEventIds, setSellerEventIds] = useState<string[]>([]);

  // Reference data
  const [eventData, setEventData] = useState<any>([]);
  const [subscriptionsData, setSubscriptionsData] = useState<any>([]);
  const [allSubscriptionPlans, setAllSubscriptionPlans] = useState<any[]>([]); // Claude: full plan objects for preview in edit modal 20-04-2026
  const [rewardPlansData, setRewardPlansData] = useState<any>([]);
  const [countryOptions, setCountryOptions] = useState<{ value: string }[]>([]);
  const [industryNodes, setTaxonomyNodes] = useState<any[]>([]);
  const [departmentOptions, setDepartmentOptions] = useState<{ label: string; value: string }[]>([]); // Claude: 16-Apr-2026
  const [currentSellerPlan, setCurrentSellerPlan] = useState<any>({});

  // Industry assignment
  const [isAssignIndustryModalOpen, setIsAssignIndustryModalOpen] = useState(false);
  const [assignIndustryIds, setAssignIndustryIds] = useState<string[]>([]);

  // Advanced search
  const emptySearch = {
    companyName: '', companyHqCity: '', companyHqState: '', companyHqCountry: '',
    companyUrl: '', companyInfo: '', companyTimezone: '',
    personName: '', personEmail: '', personPhoneNumber: '', personDesignation: '',
    productTypeTags: '', status: '', verificationStatus: '', linkedinVerified: '',
    isPlanActive: '', industryId: '', sellerCompanyType: '',
    targetCountry: '', targetIndustryId: '', targetDepartmentId: '', // Claude: target filters 18-04-2026
  };
  const [appliedSearch, setAppliedSearch] = useState({ ...emptySearch });
  const [pendingSearch, setPendingSearch] = useState({ ...emptySearch });
  const [pendingSearchModes, setPendingSearchModes] = useState<Record<string, string>>({});

  // Override verification
  const [isOverrideModalOpen, setIsOverrideModalOpen] = useState(false);
  const [overrideSellerId, setOverrideSellerId] = useState("");
  const [overrideStatus, setOverrideStatus] = useState("verified");
  const [overrideReason, setOverrideReason] = useState("");
  const [overrideNotes, setOverrideNotes] = useState<any[]>([]);
  const [overrideCurrentStatus, setOverrideCurrentStatus] = useState("");
  const [domainConflictEmail, setDomainConflictEmail] = useState<string | null>(null);

  // Select-all-DB
  const [selectAllDB, setSelectAllDB] = useState(false);
  const [excludedIds, setExcludedIds] = useState<string[]>([]);

  // Claude: Renew modal — plan + payment details 20-04-2026 / updated 25-Apr-2026
  const [isRenewModalOpen, setIsRenewModalOpen] = useState(false);
  const [renewTargetSellerId, setRenewTargetSellerId] = useState("");
  const [renewInitialPlanId, setRenewInitialPlanId] = useState<string | undefined>(undefined);
  const [renewLoading, setRenewLoading] = useState(false);
  const [editOriginalPlanId, setEditOriginalPlanId] = useState<string | null>(null); // Claude: restore on cancel 25-Apr-2026

  // Claude: column chooser state 14-04-2026
  const [visibleColKeys, setVisibleColKeys] = useState<string[]>(() => {
    try { const s = localStorage.getItem("adminSellerColumns"); return s ? JSON.parse(s) : DEFAULT_SELLER_COLS; }
    catch { return DEFAULT_SELLER_COLS; }
  });
  const [colPopoverOpen, setColPopoverOpen] = useState(false);
  const toggleCol = (key: string) => {
    const next = visibleColKeys.includes(key) ? visibleColKeys.filter(k => k !== key) : [...visibleColKeys, key];
    setVisibleColKeys(next);
    localStorage.setItem("adminSellerColumns", JSON.stringify(next));
  };

  // ── Handlers ──────────────────────────────────────────────────────────────

  const getSellerPlan = async (sellerId: string) => {
    try {
      const [planDetails] = await Promise.all([AdminAPI.getSellerProfile(sellerId)]);
      if (planDetails) setCurrentSellerPlan(planDetails?.data?.data);
    } catch (err) { console.log("Error fetching seller plan:", err); }
  };

  const handleDeleteOk = async (rows: []) => {
    setLoading(true);
    try {
      // Claude: use sellerId (seller._id) not _id (eventSeller._id in event view) 07-03-2026
      // Claude: bulk endpoint to avoid N parallel requests 24-04-2026
      const toDelete = selectAllDB ? rows.filter((item: any) => !excludedIds.includes(item._id)) : rows;
      const ids = toDelete.map((item: any) => item.sellerId || item._id);
      await AdminAPI.bulkDeleteSellers(ids);
      success("Sellers deleted successfully", "");
      getSellersData();
    } catch { error("Sellers delete failed"); }
    finally { setSelectedRowKeys([]); setSelectedRows([]); setSelectAllDB(false); setExcludedIds([]); setLoading(false); }
  };

  // Claude: unlink sellers from the filtered event only 07-Apr-2026
  const handleUnlinkOk = async (rows: []) => {
    setLoading(true);
    try {
      // Claude: in selectAllDB mode, skip explicitly excluded rows 24-04-2026
      const toUnlink = selectAllDB ? rows.filter((item: any) => !excludedIds.includes(item._id)) : rows;
      await Promise.all(toUnlink.map((item: any) =>
        AdminAPI.unlinkSellerFromEvent(item.sellerId || item._id, sortEventId)
      ));
      success("Sellers unlinked from event successfully", "");
      getSellersData();
    } catch { error("Unlink failed"); }
    finally { setSelectedRowKeys([]); setSelectedRows([]); setSelectAllDB(false); setExcludedIds([]); setLoading(false); }
  };

  const resetPasswordClick = async (rows: []) => {
    setLoading(true);
    const apiPromises: any[] = [];
    try {
      if (rows.length === 0) { error("Please select atleast one entry to reset !"); return; }
      rows.map(async (item: any) => {
        const response = await handleResetPassword(item.userId);
        apiPromises.push(response);
      });
      Promise.all(apiPromises)
        .then(() => success("Password reset successfully", ""))
        .catch(() => error("Password reset failed"));
    } catch (err) { error(errorHandler(err)); }
    finally { setLoading(false); setSelectedRowKeys([]); setSelectedRows([]); }
  };

  const handleResetPassword = async (userId: string) => {
    try { return await AdminAPI.resetPassword(userId); }
    catch (err) { throw err; }
  };

  const clearData = () => {
    setSellerEventIds([]);
    setFormData({
      eventId: "", companyName: "", companyHqCity: "", companyHqState: "",
      companyHqCountry: "", companyUrl: "", personName: "", personEmail: "",
      personPhoneNumber: "", personDesignation: "", companyTimezone: "",
      sellerBoothNumberIdentifier: [], productTypeTags: [],
      subscriptionPlanId: "", rewardPlanId: "", industryIds: [], targetIndustryIds: [],
      targetCountries: [], targetDepartments: [], // Claude: visibility targeting 16-Apr-2026
      companyInfo: "", sellerCompanyType: [], sellerCompanyTypeOther: "", adminNotes: "", // Claude: 14-04-2026 / 20-04-2026
    });
  };

  const handleDelete = async (key: any) => {
    try { return await AdminAPI.deleteSeller(key); }
    catch (err) { throw err; }
  };

  const handleChange = (e: any) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const validate = () => {
    if (mandatoryFields.some((field) => !formData[field as keyof AdminSeller])) {
      error("Please fill all the mandatory fields", ""); return false;
    }
    if (!validateEmail(formData.personEmail)) {
      error("Please enter valid email", ""); return false;
    }
    return true;
  };

  const getSellersData = async () => {
    setLoading(true);
    try {
      const params: any = { eventId: sortEventId, limit: PAGESIZE, page: currentPage };
      Object.entries(appliedSearch).forEach(([k, v]) => { if (v) params[k] = v; });
      const res = await AdminAPI.getEventSeller(params);
      if (res) { setData(res.data.data); setTotal(res.data.totalCount); }
    } catch (err) { error(errorHandler(err)); }
    finally { setLoading(false); }
  };

  // Claude: fetch events + subscription plans + reward plans + taxonomy tree on mount 27-02-2026
  const getData = async () => {
    setLoading(true);
    try {
      const [data1, data2, data3, data4, data5] = await Promise.all([
        AdminAPI.getEvent(), AdminAPI.getSellerSubscriptions(),
        AdminAPI.getIndustryTree(), AdminAPI.getRewardPlans(),
        DepartmentsAPI.getAll(), // Claude: department options 16-Apr-2026
      ]);
      if (data1?.data?.data?.length > 0) {
        setEventData(data1.data.data.map((event: any) => ({ label: event.eventName, value: event._id })));
      }
      if (data2?.data?.data?.subscriptions?.length > 0) {
        setSubscriptionsData(data2.data.data.subscriptions.map((item: any) => ({ label: item.planName, value: item._id })));
        setAllSubscriptionPlans(data2.data.data.subscriptions); // Claude: keep full plan data for dropdown preview 20-04-2026
      }
      const taxNodes = Array.isArray(data3) ? data3 : (data3?.data ?? []);
      setTaxonomyNodes(taxNodes);
      // Claude: department options 16-Apr-2026
      const deptList: any[] = Array.isArray(data5) ? data5 : (data5?.data ?? []);
      setDepartmentOptions(deptList.map((d: any) => ({ label: d.name, value: String(d._id) })));
      if (data4?.data?.data?.length > 0) {
        setRewardPlansData(data4.data.data.map((item: any) => ({ label: item.name, value: item._id })));
      }
    } catch (err) { error(errorHandler(err)); }
    finally { setLoading(false); }
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    const payload: any = { ...formData };
    if (!payload.eventId) delete payload.eventId;
    if (edit) payload.eventIds = sellerEventIds;
    if (edit) {
      setLoading(true);
      try {
        const res = await AdminAPI.putSeller(editId, payload);
        success("Seller edited successfully", "");
        clearData(); getSellersData(); handleOk(); setEdit(false);
        // Claude: if companyUrl change triggered a verification status change, prompt admin to override 10-03-2026
        if (res?.data?.verificationTriggered) {
          setOverrideSellerId(res.data.sellerId);
          setOverrideStatus(""); setOverrideReason(""); setOverrideNotes([]); setDomainConflictEmail(null);
          setIsOverrideModalOpen(true);
        } else { setEditId(""); }
      } catch (err) { error(errorHandler(err)); }
      finally { setLoading(false); }
    } else {
      const verifResult = runVerificationCheck(formData.personEmail, formData.companyUrl);
      const doCreate = async () => {
        setLoading(true);
        try {
          await AdminAPI.postSeller(payload);
          success("Seller added successfully", "");
          clearData(); getSellersData(); handleOk();
        } catch (err) { error(errorHandler(err)); }
        finally { setLoading(false); }
      };
      if (verifResult.status === "unverified") {
        Modal.confirm({
          title: "Verification Warning",
          content: `This seller will be created as unverified. Reason: ${verifResult.reason}. Continue?`,
          okText: "Continue", cancelText: "Cancel", onOk: doCreate,
        });
        return;
      }
      await doCreate();
    }
  };

  const handleBulkUpload = async () => {
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", bulkDocument);
      const res = await AdminAPI.bulkUploadUsers(fd, { role: "Seller", eventId: bulkEventId });
      if (res) { clearData(); getSellersData(); handleBulkOk(); setBulkUploadDone(true); }
    } catch (err) { error(errorHandler(err)); }
    finally { setLoading(false); }
  };

  // Claude: assign selected sellers to one or more industries — one API call per industry 20-02-2026
  const handleAssignIndustry = async () => {
    if (assignIndustryIds.length === 0) { error("Please select at least one industry"); return; }
    if (selectedRows.length === 0) { error("Please select at least one seller"); return; }
    setLoading(true);
    try {
      await Promise.all(
        assignIndustryIds.map((industryId) =>
          AdminAPI.mapSellersToIndustry({ industryId, sellerIds: selectedRows.map((r: any) => r._id) })
        )
      );
      success("Industries assigned successfully", "");
      setIsAssignIndustryModalOpen(false); setAssignIndustryIds([]);
      setSelectedRowKeys([]); setSelectedRows([]);
      getSellersData();
    } catch (err) { error(errorHandler(err)); }
    finally { setLoading(false); }
  };

  // Claude: admin verification override handler 26-02-2026
  const handleOverrideVerification = async () => {
    if (!overrideReason.trim()) { error("Please enter a reason for the override"); return; }
    setLoading(true);
    try {
      await AdminAPI.overrideSellerVerification(overrideSellerId, overrideStatus, overrideReason);
      success("Verification status updated");
      setIsOverrideModalOpen(false); setOverrideReason("");
      getSellersData();
    } catch (err) { error(errorHandler(err)); }
    finally { setLoading(false); }
  };

  // Claude: renew + payment record in one confirm 25-Apr-2026
  const handleConfirmSellerRenew = async (data: SellerRenewData) => {
    setRenewLoading(true);
    try {
      await AdminSellerAPI.renewSellerSubscription(renewTargetSellerId, { subscriptionPlanId: data.subscriptionPlanId });
      const plan = allSubscriptionPlans.find((p: any) => p._id === data.subscriptionPlanId);
      await AdminSellerAPI.addSellerPaymentRecord(renewTargetSellerId, {
        date: data.date,
        paymentReference: data.paymentReference,
        planName: data.planName,
        planAmount: plan?.subscriptionValue ?? 0,
        paidAmount: parseFloat(data.paidAmount) || 0,
        rfxAdded: plan?.rfxLimit ?? 0,
      });
      success("Subscription renewed successfully", "");
      setIsRenewModalOpen(false);
      getSellersData();
    } catch (err) { error(errorHandler(err)); }
    finally { setRenewLoading(false); }
  };

  const onSelectChange = (keys: any, rows: any) => {
    if (selectAllDB) {
      // Claude: in selectAllDB mode, track exclusions instead of clearing selectAllDB 24-04-2026
      const currentPageIds = data.map((r: any) => r._id);
      const deselected = currentPageIds.filter((id: string) => !(keys as string[]).includes(id));
      const reselected = currentPageIds.filter((id: string) => (keys as string[]).includes(id));
      setExcludedIds(prev => [...prev.filter((id: string) => !reselected.includes(id)), ...deselected.filter((id: string) => !prev.includes(id))]);
      const otherPageKeys = (selectedRowKeys as string[]).filter((id: string) => !currentPageIds.includes(id));
      setSelectedRowKeys([...otherPageKeys, ...keys] as never[]);
      return;
    }
    setSelectedRowKeys(keys); setSelectedRows(rows); setSelectAllDB(false);
  };

  const handleSelectAllFromDB = async () => {
    setLoading(true);
    try {
      const params: any = { eventId: sortEventId, limit: total, page: 1 };
      Object.entries(appliedSearch).forEach(([k, v]) => { if (v) params[k] = v; });
      const res = await AdminAPI.getEventSeller(params);
      if (res) {
        const allRows = res.data.data;
        setSelectedRows(allRows);
        setSelectedRowKeys(allRows.map((r: any) => r._id));
        setSelectAllDB(true);
      }
    } catch (err) { error(errorHandler(err)); }
    finally { setLoading(false); }
  };

  // ── Effects ───────────────────────────────────────────────────────────────

  useEffect(() => { getData(); }, []);

  useEffect(() => {
    if (skipInitialFetchRef.current) { skipInitialFetchRef.current = false; return; }
    getSellersData();
    setSelectedRowKeys([]); setSelectedRows([]); setSelectAllDB(false); setExcludedIds([]);
  }, [sortEventId, currentPage, appliedSearch]);

  // Claude: restore advanced search state when returning from edit page 25-Apr-2026
  useEffect(() => {
    const restored = (location.state as any)?.restoredSearch;
    if (restored) {
      setAppliedSearch(restored.appliedSearch ?? { ...emptySearch });
      setPendingSearch(restored.pendingSearch ?? { ...emptySearch });
      setPendingSearchModes(restored.pendingSearchModes ?? {});
      setCurrentPage(restored.currentPage ?? 1);
      setSortEventId(restored.sortEventId ?? "");
      setPendingEventId(restored.pendingEventId ?? "");
    }
  }, []);

  // ── Columns ───────────────────────────────────────────────────────────────

  // Claude: dynamic column chooser — allColumns defines every possible column; columns filters by visibleColKeys; Actions always last 14-04-2026
  const allSellerColumns: any[] = [
    {
      title: "Company Name", dataIndex: "companyName", key: "companyName",
      sorter: (a: any, b: any) => a.companyName.localeCompare(b.companyName),
    },
    { title: "Country", dataIndex: "companyHqCountry", key: "companyHqCountry" },
    { title: "City", dataIndex: "companyHqCity", key: "companyHqCity" },
    { title: "State", dataIndex: "companyHqState", key: "companyHqState" },
    { title: "Timezone", dataIndex: "companyTimezone", key: "companyTimezone", render: (v: string) => v || <Text color="gray.400" fontSize="xs">—</Text> },
    { title: "Website", dataIndex: "companyUrl", key: "companyUrl", render: (v: string) => v || <Text color="gray.400" fontSize="xs">—</Text> },
    {
      title: "Company Info", dataIndex: "companyInfo", key: "companyInfo",
      render: (v: string) => v
        ? <span title={v} style={{ display: "block", maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{v}</span>
        : <Text color="gray.400" fontSize="xs">—</Text>,
    },
    {
      title: "Industry", dataIndex: "industryIds", key: "industryIds",
      render: (ids: any[]) => {
        if (!ids?.length) return <Text color="gray.400" fontSize="xs">—</Text>;
        const names = ids.map((id: any) => industryNodes.find((n: any) => String(n._id) === String(id))?.name).filter(Boolean);
        return names.length ? <span>{names.join(", ")}</span> : <Text color="gray.400" fontSize="xs">—</Text>;
      },
    },
    {
      title: "Product Tags", dataIndex: "productTypeTags", key: "productTypeTags",
      render: (tags: string[]) => tags?.length ? <span>{tags.join(", ")}</span> : <Text color="gray.400" fontSize="xs">—</Text>,
    },
    // Claude: target filters columns 18-04-2026
    {
      title: "Target Country", dataIndex: "targetCountries", key: "targetCountries",
      render: (v: string[]) => v?.length ? <span>{v.join(", ")}</span> : <Text color="gray.400" fontSize="xs">—</Text>,
    },
    {
      title: "Target Industry", dataIndex: "targetIndustryIds", key: "targetIndustryIds",
      render: (ids: any[]) => {
        if (!ids?.length) return <Text color="gray.400" fontSize="xs">—</Text>;
        const names = ids.map((id: any) => industryNodes.find((n: any) => String(n._id) === String(id))?.name).filter(Boolean);
        return names.length ? <span>{names.join(", ")}</span> : <Text color="gray.400" fontSize="xs">—</Text>;
      },
    },
    {
      title: "Target Department", dataIndex: "targetDepartments", key: "targetDepartments",
      render: (ids: any[]) => {
        if (!ids?.length) return <Text color="gray.400" fontSize="xs">—</Text>;
        const names = ids.map((id: any) => departmentOptions.find((d: any) => d.value === String(id))?.label).filter(Boolean);
        return names.length ? <span>{names.join(", ")}</span> : <Text color="gray.400" fontSize="xs">—</Text>;
      },
    },
    { title: "Person Name", dataIndex: "personName", key: "personName" },
    { title: "Email", dataIndex: "personEmail", key: "personEmail" },
    { title: "Phone", dataIndex: "personPhoneNumber", key: "personPhoneNumber" },
    { title: "Designation", dataIndex: "personDesignation", key: "personDesignation", render: (v: string) => v || <Text color="gray.400" fontSize="xs">—</Text> },
    {
      title: "LinkedIn Verified", dataIndex: "linkedinVerified", key: "linkedinVerified",
      render: (v: any) => v ? <span style={{ color: "#2E7D32" }}>Yes</span> : <span style={{ color: "#999" }}>No</span>,
    },
    {
      title: "Subscription Plan", dataIndex: "planName", key: "planName",
      render: (planName: string) => planName || <Text color="gray.400" fontSize="xs">—</Text>,
    },
    {
      title: "Plan Active", dataIndex: "isPlanActive", key: "isPlanActive",
      render: (v: any) => v ? <span style={{ color: "#2E7D32" }}>Yes</span> : <span style={{ color: "#856404" }}>No</span>,
    },
    {
      title: "Company Type", dataIndex: "sellerCompanyType", key: "sellerCompanyType",
      render: (_: any, object: any) => {
        const types: string[] = object.sellerCompanyType || [];
        if (!types.length) return <Text color="gray.400" fontSize="xs">—</Text>;
        const display = types.includes("Others") && object.sellerCompanyTypeOther
          ? [...types.filter((t: string) => t !== "Others"), object.sellerCompanyTypeOther]
          : types;
        return <span>{display.join(", ")}</span>;
      },
    },
    {
      title: "Verification", dataIndex: "verificationStatus", key: "verificationStatus",
      render: (_: any, object: any) => {
        const status = object.verificationStatus || "unverified";
        const colorMap: Record<string, string> = {
          verified: "#2E7D32", unverified: "#856404", provisional: "#4F46E5",
          blocked: "#C62828", resubmitted: "#1565C0", pendingNewUser: "#E65100",
        };
        return (
          <Flex gap={1} alignItems="center" flexWrap="wrap">
            <span style={{ color: colorMap[status] || "#999" }}>{status}</span>
            <Button size="xs" h="20px" px={2} minW="fit-content"
              background="#0E122F" color="white" fontSize="11px" _hover={{ background: "#2E7D32" }}
              onClick={() => {
                setOverrideSellerId(object._id || object.sellerId);
                setOverrideStatus(""); setOverrideReason("");
                setOverrideNotes(object.verificationNotes || []);
                setOverrideCurrentStatus(object.verificationStatus || "");
                setDomainConflictEmail(null);
                setIsOverrideModalOpen(true);
              }}
            >
              Override
            </Button>
          </Flex>
        );
      },
    },
    {
      title: "Actions", dataIndex: "actions", key: "actions",
      render: (_: any, object: any) => (
        <Flex gap={1}>
          <Button size="xs" fontSize="xs" background="#0E122F" color="white" px={2} _hover={{ background: "#2E7D32" }}
            onClick={() => {
              const searchState = { appliedSearch, pendingSearch, pendingSearchModes, currentPage, sortEventId, pendingEventId };
              navigate(`/admin/sellers/${object.sellerId}/edit`, {
                state: {
                  formData: {
                    eventId: object.eventId, companyName: object.companyName,
                    companyHqCity: object.companyHqCity, companyHqState: object.companyHqState,
                    companyHqCountry: object.companyHqCountry, companyTimezone: object.companyTimezone,
                    companyUrl: object.companyUrl, personName: object.personName,
                    personEmail: object.personEmail, personPhoneNumber: object.personPhoneNumber,
                    personDesignation: object.personDesignation,
                    sellerBoothNumberIdentifier: object.sellerBoothNumberIdentifier,
                    productTypeTags: object.productTypeTags,
                    subscriptionPlanId: object.subscriptionPlanId,
                    rewardPlanId: object.rewardPlanId,
                    industryIds: object.industryIds?.map(String) ?? [],
                    targetIndustryIds: object.targetIndustryIds?.map(String) ?? [],
                    companyInfo: object.companyInfo ?? "",
                    sellerCompanyType: object.sellerCompanyType ?? [],
                    sellerCompanyTypeOther: object.sellerCompanyTypeOther ?? "",
                    visibilityMode: object.visibilityMode ?? "open",
                    targetVerifiedOnly: object.targetVerifiedOnly ?? false,
                    targetBuyerList: object.targetBuyerList ?? false,
                    targetCountries: object.targetCountries ?? [],
                    targetDepartments: (object.targetDepartments ?? []).map(String),
                    adminNotes: object.adminNotes ?? "",
                  },
                  searchState,
                },
              });
            }}
          >
            Edit
          </Button>
          {/* Claude: Renew — opens plan-select modal; assigns plan + resets counters + restores isPlanActive 20-04-2026 */}
          {object.subscriptionPlanId && object.isPlanActive === false && (
            <Button size="xs" fontSize="xs" background="orange.500" color="white" px={2} _hover={{ background: "orange.600" }}
              onClick={() => {
                setRenewTargetSellerId(object._id || object.sellerId);
                setRenewInitialPlanId(object.subscriptionPlanId);
                setIsRenewModalOpen(true);
              }}
            >
              Renew
            </Button>
          )}
        </Flex>
      ),
    },
  ];
  const columns = [
    ...allSellerColumns.filter(col => col.key !== "actions" && (FIXED_SELLER_COLS.has(col.key) || visibleColKeys.includes(col.key))),
    allSellerColumns.find(col => col.key === "actions")!,
  ];
  const colChooserContent = (
    <Box p={1} style={{ display: "flex", flexDirection: "column", gap: 6, minWidth: 170 }}>
      {allSellerColumns
        .filter(col => !FIXED_SELLER_COLS.has(col.key))
        .map(col => (
          <Checkbox key={col.key} checked={visibleColKeys.includes(col.key)} onChange={() => toggleCol(col.key)} style={{ fontSize: 12 }}>
            {col.title}
          </Checkbox>
        ))
      }
    </Box>
  );

  const rowSelection = { selectedRowKeys, onChange: onSelectChange };
  const generateRowKey = (record: any) => record._id;

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="p-4">
      {loading ? <Loader /> : null}

      <SellerDeleteModal
        isOpen={isDeleteModalOpen}
        onClose={handleDeleteModalOk}
        sortEventId={sortEventId}
        selectAllDB={selectAllDB}
        total={total}
        excludedIds={excludedIds}
        selectedRowKeys={selectedRowKeys}
        onConfirmDelete={() => { handleDeleteOk(selectedRows); handleDeleteModalOk(); }}
        onConfirmUnlink={() => { handleUnlinkOk(selectedRows); handleDeleteModalOk(); }}
      />

      <SellerAssignIndustryModal
        isOpen={isAssignIndustryModalOpen}
        onClose={() => { setIsAssignIndustryModalOpen(false); setAssignIndustryIds([]); }}
        industryNodes={industryNodes}
        assignIndustryIds={assignIndustryIds}
        setAssignIndustryIds={setAssignIndustryIds}
        selectedRowCount={selectedRows.length}
        onAssign={handleAssignIndustry}
        loading={loading}
      />

      <SellerOverrideModal
        isOpen={isOverrideModalOpen}
        onClose={() => setIsOverrideModalOpen(false)}
        overrideNotes={overrideNotes}
        overrideSellerId={overrideSellerId}
        overrideStatus={overrideStatus}
        setOverrideStatus={setOverrideStatus}
        overrideReason={overrideReason}
        setOverrideReason={setOverrideReason}
        domainConflictEmail={domainConflictEmail}
        setDomainConflictEmail={setDomainConflictEmail}
        overrideCurrentStatus={overrideCurrentStatus}
        onConfirm={handleOverrideVerification}
        loading={loading}
      />

      <SellerBulkUploadModal
        isOpen={isBulkModalVisible}
        onClose={handleBulkOk}
        bulkDocument={bulkDocument}
        setBulkDocument={setBulkDocument}
        fileInputKey={fileInputKey}
        onShowConfirm={showBulkConfirmModal}
        isConfirmOpen={isBulkConfirmModalVisible}
        onConfirmClose={hideBulkConfirmModal}
        onConfirmSubmit={handleBulkUpload}
        bulkUploadDone={bulkUploadDone}
        eventName={eventName}
      />

      {/* Claude: Renew subscription modal — plan + payment details 25-Apr-2026 */}
      <SellerRenewModal
        isOpen={isRenewModalOpen}
        onClose={() => setIsRenewModalOpen(false)}
        sellerId={renewTargetSellerId}
        plansData={allSubscriptionPlans}
        initialPlanId={renewInitialPlanId}
        onConfirm={handleConfirmSellerRenew}
        loading={renewLoading}
      />


      {/* Claude: toolbar — advanced search always visible; companyName moved inside panel 20-05-2026 */}
      <Box mt={1} mb={1}>
        <Text fontSize="md" fontWeight={600} mb={1}>Sellers</Text>
        <Flex gap={2} alignItems="flex-end" flexWrap="wrap">
          {(sortEventId || Object.entries(appliedSearch).some(([_, v]) => v)) && (
            <Text fontSize="xs" color="blue.500" mb="4px">Filters active</Text>
          )}
          <Box flex={1} />
          <Button size="sm" fontSize="xs" h="28px" background="#0E122F" color="white" px={3} gap={2}
            _hover={{ background: "#2E7D32" }}
            onClick={() => navigate('/admin/sellers/new', { state: { searchState: { appliedSearch, pendingSearch, pendingSearchModes, currentPage, sortEventId, pendingEventId } } })}
          >
            <AiOutlinePlus /> Seller
          </Button>
          <Button size="sm" fontSize="xs" h="28px" background="#0E122F" color="white" px={3}
            _hover={{ background: "#2E7D32" }} onClick={showBulkModal}
          >
            Bulk Upload
          </Button>
          <Button size="sm" fontSize="xs" h="28px" background="#0E122F" color="white" px={3}
            _hover={{ background: "#2E7D32" }}
            onClick={() => {
              const params: any = { eventId: sortEventId };
              Object.entries(appliedSearch).forEach(([k, v]) => { if (v) params[k] = v; });
              AdminAPI.exportSellers(params);
            }}
          >
            Export CSV
          </Button>
        </Flex>

        <SellerAdvancedSearch
          pendingSearch={pendingSearch}
          setPendingSearch={setPendingSearch}
          pendingSearchModes={pendingSearchModes}
          setPendingSearchModes={setPendingSearchModes}
          pendingEventId={pendingEventId}
          setPendingEventId={setPendingEventId}
          emptySearch={emptySearch}
          eventData={eventData}
          industryNodes={industryNodes}
          departmentOptions={departmentOptions}
          onReset={() => {
            setPendingSearch({ ...emptySearch });
            setAppliedSearch({ ...emptySearch });
            setPendingSearchModes({});
            setPendingEventId("");
            setSortEventId("");
            setCurrentPage(1);
          }}
          onApply={() => {
            setAppliedSearch({ ...pendingSearch });
            setSortEventId(pendingEventId);
            setCurrentPage(1);
          }}
        />
      </Box>

      <Box mt={1}>
        {/* Claude: bulk actions row + count + select-all-DB banner 02-03-2026 */}
        <Flex justifyContent="start" mb={1} gap={1} alignItems="center">
          <Button size="xs" fontSize="xs" background="#0E122F" color="white" px={3}
            _hover={{ background: "#2E7D32" }} onClick={() => resetPasswordClick(selectedRows)}
          >
            Reset Password
          </Button>
          {/* Claude: Assign Industry button — moved before Delete to match Buyer page order 23-02-2026 */}
          <Button size="xs" fontSize="xs" background="#0E122F" color="white" px={3}
            _hover={{ background: "#2E7D32" }} leftIcon={<MdOutlineAccountTree />}
            onClick={() => { setAssignIndustryIds([]); setIsAssignIndustryModalOpen(true); }}
          >
            Assign Industry
          </Button>
          <Button size="xs" fontSize="xs" background="secondary.light" color="secondary.main" px={3}
            _hover={{ background: "#C62828", color: "white" }}
            onClick={() => {
              if (selectedRows.length > 0) showDeleteModal();
              else error("Please select atleast one entry to delete !");
            }}
          >
            Delete
          </Button>
          <Text fontSize="xs" color="gray.500" ml={2}>
            {total === 0 ? "No sellers" : `${total} sellers`}
          </Text>
          {/* Claude: column chooser button — right-aligned 14-04-2026 */}
          <Popover content={colChooserContent} trigger="click" open={colPopoverOpen} onOpenChange={setColPopoverOpen} placement="bottomRight">
            <Button size="xs" fontSize="xs" background="#0E122F" color="white" px={3} ml="auto" _hover={{ background: "#2E7D32" }}>
              Columns
            </Button>
          </Popover>
        </Flex>

        {/* Select-all-DB banner */}
        {((selectedRowKeys.length === data.length && data.length > 0) || selectAllDB) && (
          <Flex mb={1} px={2} py={1} bg="blue.50" borderRadius="md" alignItems="center" gap={2} fontSize="xs">
            {selectAllDB ? (
              <>
                <Text>All <strong>{total - excludedIds.length}</strong> sellers are selected.</Text>
                <Text color="blue.600" cursor="pointer" textDecoration="underline"
                  onClick={() => { setSelectedRowKeys([]); setSelectedRows([]); setSelectAllDB(false); setExcludedIds([]); }}
                >
                  Clear selection
                </Text>
              </>
            ) : (
              <>
                <Text><strong>{data.length}</strong> sellers on this page are selected.</Text>
                {total > data.length && (
                  <Text color="blue.600" cursor="pointer" textDecoration="underline" onClick={handleSelectAllFromDB}>
                    Select all {total} sellers from the database
                  </Text>
                )}
              </>
            )}
          </Flex>
        )}

        <Box sx={{ "& .ant-table": { fontSize: "12px" }, "& .ant-table-cell": { fontSize: "12px" } }}>
          <Table
            size="small"
            style={{ width: "100%" }}
            tableLayout="fixed"
            rowSelection={{ type: "checkbox", ...rowSelection }}
            dataSource={data}
            bordered
            columns={columns}
            pagination={false}
            rowKey={generateRowKey}
          />
        </Box>
        <Flex justifyContent="end">
          <Pagination
            current={currentPage}
            total={total}
            onChange={(number) => setCurrentPage(number)}
            pageSize={PAGESIZE}
          />
        </Flex>
      </Box>
    </div>
  );
};

export default Sellers;
